import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

/**
 * Global "Mostra dati demo" toggle for the admin panel.
 *
 * The default across the whole admin surface is REAL DATA ONLY:
 *   • Every admin endpoint filters `is_demo != true` unless the client
 *     passes `include_demo=true`.
 *   • This context exposes the current toggle state plus a helper
 *     `demoParams()` that returns `{ include_demo: true }` when the
 *     toggle is ON so callers can spread it into their axios params
 *     without touching every URL string.
 *   • Persisted in `sessionStorage` so refreshing keeps the choice
 *     without leaking into future browser sessions.
 */
const STORAGE_KEY = "autexio_admin_include_demo";

const AdminDemoContext = createContext({
  includeDemo: false,
  setIncludeDemo: () => {},
  demoParams: () => ({}),
});

export function AdminDemoProvider({ children }) {
  const [includeDemo, _setIncludeDemo] = useState(() => {
    try {
      return typeof window !== "undefined" && window.sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const setIncludeDemo = useCallback((next) => {
    _setIncludeDemo(next);
    try {
      if (next) window.sessionStorage.setItem(STORAGE_KEY, "1");
      else window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  // Notify sibling admin windows/tabs of the toggle change so counters
  // stay coherent if the admin opens the panel in two tabs.
  useEffect(() => {
    const listener = (ev) => {
      if (ev.key === STORAGE_KEY) {
        _setIncludeDemo(ev.newValue === "1");
      }
    };
    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  }, []);

  const demoParams = useCallback(
    () => (includeDemo ? { include_demo: true } : {}),
    [includeDemo],
  );

  return (
    <AdminDemoContext.Provider value={{ includeDemo, setIncludeDemo, demoParams }}>
      {children}
    </AdminDemoContext.Provider>
  );
}

export function useAdminDemo() {
  return useContext(AdminDemoContext);
}
